import React, { useState, useEffect } from 'react';

import TableHeader from './TableHeader'
import TableRows from './TableRows'
import TablePaging from './TablePaging'

import { useNavigate, useLocation } from 'react-router-dom';

import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

export default function TableFull({ data, columns, styles, onRowClick, dropDownMenuActions, resetSelection, initialStateSettings, mainTableClassName, allowColumnHeaderSort, allowGlobalFilter, globalFilterText, globalFilterFn: customGlobalFilterFn, rowSelection: externalRowSelection, setRowSelection: externalSetRowSelection, expanded: externalExpanded, onExpandedChange: externalOnExpandedChange, renderExpandedRow, getRowId }) {

  const [internalRowSelection, setInternalRowSelection] = React.useState({})
  const [resetRowSelection, setResetRowSelection] = React.useState(0);

  // Use external row selection if provided, otherwise use internal
  const rowSelection = externalRowSelection !== undefined ? externalRowSelection : internalRowSelection;
  const setRowSelection = externalSetRowSelection || setInternalRowSelection;

  // Expanded rows ({ [rowId]: true }), controlled by the caller when it passes `expanded`.
  // onExpandedChange receives a TanStack updater, so a React state setter fits it as is.
  const [internalExpanded, setInternalExpanded] = React.useState({});
  const expanded = externalExpanded !== undefined ? externalExpanded : internalExpanded;
  const setExpanded = externalOnExpandedChange || setInternalExpanded;

  const [sorting, setSorting] = React.useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const useQuery = () => {
    return new URLSearchParams(location.search);
  };

  const QUERY_PARAM_PAGE_INDEX = 'page';
  const QUERY_PARAM_PAGE_SIZE = 'size';

  const query = useQuery();
  const currentPage = query.get(QUERY_PARAM_PAGE_INDEX) ? parseInt(query.get(QUERY_PARAM_PAGE_INDEX), 10) - 1 : 0;
  const currentPageSize = query.get(QUERY_PARAM_PAGE_SIZE) ? parseInt(query.get(QUERY_PARAM_PAGE_SIZE), 10) : 10;

  const setPageAndSize = (pageNumber, pageSize) => {
    if (currentPage !== pageNumber)
      query.set(QUERY_PARAM_PAGE_INDEX, pageNumber + 1);

    if (currentPageSize !== pageSize)
      query.set(QUERY_PARAM_PAGE_SIZE, pageSize);

    if (currentPage !== pageNumber || currentPageSize !== pageSize)
    navigate({ search: query.toString() });
  };

  const [pagination, setPagination] = useState({
    pageIndex: currentPage, //initial page index
    pageSize: currentPageSize, //default page size
  });

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
      pagination,
      sorting,
      expanded,
      globalFilter: globalFilterText?.trim(),
    },
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    // Flat rows have no subRows, so TanStack would treat them as not expandable.
    getRowCanExpand: renderExpandedRow ? () => true : undefined,
    getRowId,
    //onGlobalFilterChange: setGlobalFilter,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: (updater) => {
      // make sure updater is callable (to avoid typescript warning)
      if (typeof updater !== "function") return;

      const newPageInfo = updater(table.getState().pagination);
      //output: {pageIndex: 1, pageSize: 30}
      setPagination(newPageInfo);
      setPageAndSize(newPageInfo.pageIndex, newPageInfo.pageSize);
    },
    autoResetPageIndex: false,
    initialState: initialStateSettings,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    globalFilterFn: allowGlobalFilter ? (customGlobalFilterFn || 'includesString') : undefined,
  });

  useEffect(() => {
    // Bubble up the selection to the parent component        
    if (resetSelection !== resetRowSelection) {
      table.toggleAllRowsSelected(false);
      setResetRowSelection(resetSelection);
    }
    //setPagination({ pageIndex: currentPage });
    table.setPagination({ pageIndex: currentPage, pageSize: currentPageSize });
    //table.setPageSize(currentPageSize);
    //setHiddenColumns(CurrentUserService.hasDeleteRecordPerimssion() ? '' : 'selection')
  }, [rowSelection, resetSelection, location]);

  return (
    <>
      <div className={`${mainTableClassName ?? styles.table}`}>

        <TableHeader table={table} styles={styles} allowColumnHeaderSort={allowColumnHeaderSort} />

        <TableRows table={table} styles={styles} isRowHover={true} onRowClick={onRowClick} dropDownMenuActions={dropDownMenuActions} renderExpandedRow={renderExpandedRow} />

      </div>

      <TablePaging table={table} />
    </>
  )
}